import axios from 'axios';
import { API_URL, GUEST_TOKEN } from '../config';

// 创建 axios 实例
const articleApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 添加请求拦截器，自动添加 token
articleApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || GUEST_TOKEN;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 文章服务
const articleService = {
  // 获取文章列表
  getArticles: async (page = 1, pageSize = 9, categoryId = null) => {
    try {
      const params = {
        populate: ['category', 'cover', 'authors'],
        sort: ['publishedAt:desc'],
        'pagination[page]': page,
        'pagination[pageSize]': pageSize
      };
      
      if (categoryId) {
        params['filters[category][id][$eq]'] = categoryId;
      }
      
      const response = await articleApi.get('/api/articles', { params });
      return response.data;
    } catch (error) {
      console.error('获取文章列表失败:', error);
      throw error;
    }
  },

  // 获取单个文章
  getArticle: async (slug) => {
    try {
      const response = await articleApi.get(`/api/articles`, {
        params: {
          'filters[slug][$eq]': slug,
          populate: ['category', 'cover', 'authors', 'blocks']
        }
      });
      return response.data.data[0]; // 返回第一个匹配的文章
    } catch (error) {
      console.error('获取文章详情失败:', error);
      throw error;
    }
  },

  // 创建文章
  createArticle: async (articleData) => {
    try {
      console.log('🔍 开始创建文章:', articleData);

      // 构建文章数据，包含blocks字段
      const requestData = {
        data: {
          title: articleData.title,
          description: articleData.description,
          content: articleData.content,
          slug: articleData.slug,
          category: articleData.category || null,
          cover: articleData.cover || null,
          publishedAt: articleData.publishedAt || new Date().toISOString()
        }
      };

      // 如果有author数据，添加到请求中 - 使用正确的manyToOne关系格式
      if (articleData.author) {
        requestData.data.author = articleData.author;
        console.log('🔍 包含author数据 (manyToOne格式):', articleData.author);
      }

      // 如果有blocks数据，添加到请求中
      if (articleData.blocks && articleData.blocks.length > 0) {
        requestData.data.blocks = articleData.blocks;
        console.log('🔍 包含blocks数据:', articleData.blocks);
      }

      const response = await articleApi.post('/api/articles', requestData);
      console.log('🔍 文章创建响应:', response);
      return response.data.data;
    } catch (error) {
      console.error('❌ 创建文章失败:', error);
      console.error('❌ 响应详情:', error.response?.data);
      throw error;
    }
  },

  // 更新文章
  updateArticle: async (documentId, articleData) => {
    try {
      if (!documentId) {
        throw new Error(`Invalid documentId: ${documentId}`);
      }

      // 构建更新数据
      const requestData = {
        data: {
          title: articleData.title,
          description: articleData.description,
          content: articleData.content,
          slug: articleData.slug,
          category: articleData.category || null,
          cover: articleData.cover || null,
          publishedAt: articleData.publishedAt || new Date().toISOString()
        }
      };

      // 如果有blocks数据，添加到请求中
      if (articleData.blocks && articleData.blocks.length > 0) {
        requestData.data.blocks = articleData.blocks;
        console.log('🔍 更新包含blocks数据:', articleData.blocks);
      }

      const response = await articleApi.put(`/api/articles/${documentId}`, requestData);
      return response.data.data;
    } catch (error) {
      console.error('更新文章失败:', error);
      throw error;
    }
  },

  // 删除媒体文件
  deleteMedia: async (fileId) => {
    try {
      const token = localStorage.getItem('token') || GUEST_TOKEN;

      console.log(`🗑️ 尝试删除媒体文件: ${fileId}`);
      console.log(`🔑 使用token: ${token ? token.substring(0, 20) + '...' : 'null'}`);
      console.log(`🌐 API URL: ${API_URL}/api/upload/files/${fileId}`);

      const response = await fetch(`${API_URL}/api/upload/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log(`📋 删除媒体文件响应状态: ${response.status} ${response.statusText}`);
      console.log(`📋 响应headers:`, Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ 删除媒体文件失败:`, {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          fileId: fileId,
          url: `${API_URL}/api/upload/files/${fileId}`
        });
        throw new Error(`删除媒体文件失败: ${response.status} ${response.statusText}`);
      }

      // 检查响应是否有内容
      const contentType = response.headers.get('content-type');
      let result = null;

      if (contentType && contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const text = await response.text();
        console.log(`📋 响应内容 (非JSON): ${text}`);
        result = { message: text || '删除成功' };
      }

      console.log(`✅ 成功删除媒体文件: ${fileId}`, result);
      return result;
    } catch (error) {
      console.error(`❌ 删除媒体文件异常: ${fileId}`, error);
      console.error(`❌ 错误详情:`, {
        message: error.message,
        stack: error.stack,
        fileId: fileId
      });
      throw error;
    }
  },

  // 收集文章中所有的媒体文件ID
  collectMediaFileIds: (articleData) => {
    const fileIds = [];

    console.log('🔍 开始收集媒体文件ID，articleData:', articleData);

    // 安全检查，确保articleData存在
    if (!articleData) {
      console.warn('collectMediaFileIds: articleData is undefined');
      return fileIds;
    }

    // 收集封面图片
    console.log('🔍 检查封面数据:', articleData.cover);
    if (articleData.cover && articleData.cover.id) {
      fileIds.push(articleData.cover.id);
      console.log('✅ 找到封面媒体文件ID:', articleData.cover.id);
    } else if (articleData.cover && articleData.cover.data && articleData.cover.data.id) {
      // 兼容旧的数据格式
      fileIds.push(articleData.cover.data.id);
      console.log('✅ 找到封面媒体文件ID (旧格式):', articleData.cover.data.id);
    } else {
      console.log('❌ 没有找到封面媒体文件');
    }

    // 收集blocks中的媒体文件
    console.log('🔍 检查blocks数据:', articleData.blocks);
    if (articleData.blocks && Array.isArray(articleData.blocks)) {
      console.log(`🔍 发现 ${articleData.blocks.length} 个blocks`);
      articleData.blocks.forEach((block, index) => {
        console.log(`🔍 处理block ${index}:`, block.__component, block);

        switch (block.__component) {
          case 'shared.media':
            console.log('🔍 检查shared.media组件的file字段:', block.file);
            if (block.file?.id) {
              fileIds.push(block.file.id);
              console.log('✅ 找到媒体文件ID:', block.file.id);
            } else {
              console.log('❌ shared.media组件没有找到file.id');
            }
            break;
          case 'shared.slider':
            console.log('🔍 检查shared.slider组件的files字段:', block.files);
            if (block.files && Array.isArray(block.files)) {
              block.files.forEach((file, fileIndex) => {
                console.log(`🔍 检查slider文件 ${fileIndex}:`, file);
                if (file.id) {
                  fileIds.push(file.id);
                  console.log('✅ 找到slider媒体文件ID:', file.id);
                }
              });
            } else {
              console.log('❌ shared.slider组件没有找到files数组');
            }
            break;
          default:
            console.log('🔍 跳过非媒体组件:', block.__component);
        }
      });
    } else {
      console.log('❌ 没有找到blocks数组或blocks不是数组');
    }

    const uniqueFileIds = [...new Set(fileIds)]; // 去重
    console.log('🎯 收集到的媒体文件ID列表:', uniqueFileIds);
    return uniqueFileIds;
  },

  // 删除文章（包含媒体文件清理）
  deleteArticle: async (documentId) => {
    try {
      if (!documentId) {
        throw new Error(`Invalid documentId: ${documentId}`);
      }

      // 首先获取文章详情，收集所有媒体文件
      const articleResponse = await articleApi.get(`/api/articles/${documentId}`, {
        params: {
          populate: [
            'cover',
            'blocks.file',
            'blocks.slides.media'
          ]
        }
      });

      console.log('📋 API响应完整数据:', articleResponse.data);

      const articleData = articleResponse.data.data;
      if (!articleData) {
        throw new Error('文章不存在');
      }

      // Strapi 5中数据直接在顶层，不再嵌套在attributes中
      const articleAttributes = articleData; // 直接使用articleData
      console.log('📋 文章属性数据:', articleAttributes);
      console.log('📋 文章封面数据:', articleAttributes.cover);
      console.log('📋 文章blocks数据:', articleAttributes.blocks);

      // 收集所有媒体文件ID
      const mediaFileIds = articleService.collectMediaFileIds(articleAttributes);
      console.log('🗑️ 准备删除的媒体文件:', mediaFileIds);

      // 删除所有关联的媒体文件
      const deletePromises = mediaFileIds.map(async (fileId) => {
        try {
          await articleService.deleteMedia(fileId);
          console.log(`✅ 成功删除媒体文件: ${fileId}`);
        } catch (error) {
          console.warn(`⚠️ 删除媒体文件失败 ${fileId}:`, error);
          // 继续删除其他文件，不因为单个文件删除失败而中断
        }
      });

      // 等待所有媒体文件删除完成
      await Promise.allSettled(deletePromises);

      // 最后删除文章记录
      const response = await articleApi.delete(`/api/articles/${documentId}`);
      console.log('✅ 文章删除成功，包含媒体文件清理');

      return response.data;
    } catch (error) {
      console.error('删除文章失败:', error);
      throw error;
    }
  },

  // 上传媒体文件
  uploadMedia: async (file, path = 'Articles') => {
    try {
      const token = localStorage.getItem('token') || GUEST_TOKEN;
      const formData = new FormData();
      formData.append('files', file);
      formData.append('path', path);

      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const result = await response.json();
      return result[0];
    } catch (error) {
      console.error('上传媒体文件失败:', error);
      throw error;
    }
  },

  // 获取author：优先使用用户关联的author信息
  getOrCreateAuthor: async (user) => {
    console.log('🔍 查找author for user:', user.username);

    try {
      // 1. 优先检查用户是否已关联author信息（最可靠的方式）
      if (user?.author?.id) {
        console.log('✅ 用户已关联author信息:', user.author);
        return user.author.id;
      }

      console.log('⚠️ 用户未关联author信息，提示需要开通作者权限');

      // 2. 如果用户没有关联author，抛出有意义的错误提示
      throw new Error('您的账户尚未开通作者权限，请联系管理员开通作者身份后再创建文章。');

    } catch (error) {
      console.error('❌ 获取author失败:', error.message);

      // 3. 特殊情况：如果是因为没有author权限，返回null而不是undefined
      if (error.message.includes('作者权限')) {
        return null;
      }

      // 4. 其他意外错误，返回null确保文章创建不会完全失败
      return null;
    }
  },

  // 检查用户是否有author权限（辅助方法）
  hasAuthorPermission: (user) => {
    return !!(user?.author?.id);
  },

  // 获取用户的author信息（辅助方法）
  getUserAuthor: (user) => {
    return user?.author || null;
  },

  // 获取分类列表
  getCategories: async () => {
    try {
      const response = await articleApi.get('/api/categories');
      return response.data.data;
    } catch (error) {
      console.error('获取分类列表失败:', error);
      throw error;
    }
  },

  // 根据ID获取单个分类
  getCategoryById: async (id) => {
    try {
      // 首先尝试直接使用ID获取（可能是documentId）
      try {
        const response = await articleApi.get(`/api/categories/${id}`);
        return response.data;
      } catch (directError) {
        // 如果直接获取失败，可能是数字ID，需要先获取分类列表找到对应的documentId
        if (directError.response?.status === 404) {
          const categoriesResponse = await articleApi.get('/api/categories');
          const categories = categoriesResponse.data.data;
          const category = categories.find(cat => cat.id === id);
          
          if (category) {
            // 使用找到的分类的documentId重新获取
            const response = await articleApi.get(`/api/categories/${category.documentId}`);
            return response.data;
          }
        }
        throw directError;
      }
    } catch (error) {
      console.error('获取分类失败:', error);
      throw error;
    }
  }
};

export default articleService;